import React, { FunctionComponent, useContext, useEffect } from 'react';
import {
  ChatScrollWrapper,
  ChatInput,
  ChatComments,
  ChatLoginPrompt,
  ChatEmpty
} from 'features/chat/components';
import { Loader } from 'components/Loader';
import { useRecentMessages } from 'features/chat/chatHooks';
import { useCurrentUser } from 'features/current-user/hooks';
import { UserAgentContext } from 'features/user-agent/userAgent';
import { usePageVisibility } from 'hooks/usePageVisibility';
import { fetchChatHistory } from 'features/chat/chatSlice';
import { useDispatch, useSelector } from 'react-redux';
import { usePageLoaded } from 'hooks/usePageLoaded';
import { SEOHeaderTags } from 'components/SEO/SEOHeaderTags';
import { Error404 } from 'components/ErrorPages/Error404';
import { useAppSelector } from 'hooks/redux';
import { WebSocketContext } from 'features/hub/hub';
import { RootState } from 'rootReducer';
import { ChatMenu } from './ChatMenu';

export const ChannelChat: FunctionComponent = () => {
  const isLoaded = useAppSelector((state) => state.chat.isLoaded);
  const channelId = useAppSelector(
    (state) => state.channel.channelDetails?.ownerId
  );
  const currentUser = useCurrentUser();
  const {
    isNativeAppWebview,
    isEmbed,
    isDACreatorAppVersion,
    isMobileAppWebview
  } = useContext(UserAgentContext);
  const { joinCrowd } = useContext(WebSocketContext);
  const messages = useRecentMessages(isEmbed);
  const isPageVisible = usePageVisibility();
  const dispatch = useDispatch();
  const isNativeDAView = isDACreatorAppVersion(
    `${process.env.REACT_APP_DA_NATIVE_MIN_VERSION}`
  );

  // Hub socket states
  const isHubConnected = useSelector(
    (state: RootState) => state.hub.isHubConnected
  );
  const isHubCrowdConnectedUnauthenticated = useSelector(
    (state: RootState) => state.hub.isHubCrowdConnectedUnauthenticated
  );
  const isHubCrowdConnectedAuthenticated = useSelector(
    (state: RootState) => state.hub.isHubCrowdConnectedAuthenticated
  );
  const isHubAuthenticated = useSelector(
    (state: RootState) => state.hub.isHubAuthenticated
  );

  usePageLoaded();

  useEffect(() => {
    if (channelId && isPageVisible) {
      dispatch(fetchChatHistory(channelId, isEmbed || isNativeDAView));
    }
  }, [dispatch, channelId, isPageVisible, isEmbed, isNativeDAView]);

  // Join Hub crowd for native apps
  useEffect(() => {
    if (
      isNativeAppWebview &&
      isHubConnected &&
      !isHubCrowdConnectedUnauthenticated &&
      !isHubCrowdConnectedAuthenticated
    ) {
      joinCrowd(isHubAuthenticated);
    }
  }, [
    isNativeAppWebview,
    isHubConnected,
    isHubCrowdConnectedUnauthenticated,
    isHubCrowdConnectedAuthenticated,
    isHubAuthenticated,
    joinCrowd
  ]);

  // Upgrade Hub crowd to authenticated
  useEffect(() => {
    if (
      isNativeAppWebview &&
      isHubAuthenticated &&
      isHubCrowdConnectedUnauthenticated &&
      !isHubCrowdConnectedAuthenticated
    ) {
      joinCrowd(true);
    }
  }, [
    isNativeAppWebview,
    isHubAuthenticated,
    isHubCrowdConnectedUnauthenticated,
    isHubCrowdConnectedAuthenticated,
    joinCrowd
  ]);

  // Join crowd for native apps (now using same socket as web)
  useEffect(() => {
    if (
      isNativeAppWebview &&
      isHubConnected &&
      !isHubCrowdConnectedUnauthenticated &&
      !isHubCrowdConnectedAuthenticated
    ) {
      joinCrowd(isHubAuthenticated);
    }
  }, [
    isNativeAppWebview,
    isHubConnected,
    isHubCrowdConnectedUnauthenticated,
    isHubCrowdConnectedAuthenticated,
    isHubAuthenticated,
    joinCrowd
  ]);

  // Upgrade crowd to authenticated for native apps
  useEffect(() => {
    if (
      isNativeAppWebview &&
      isHubAuthenticated &&
      isHubCrowdConnectedUnauthenticated &&
      !isHubCrowdConnectedAuthenticated
    ) {
      joinCrowd(true);
    }
  }, [
    isNativeAppWebview,
    isHubAuthenticated,
    isHubCrowdConnectedUnauthenticated,
    isHubCrowdConnectedAuthenticated,
    joinCrowd
  ]);

  const renderChatInput = () => {
    if (isEmbed) {
      return null;
    } else if (currentUser && channelId) {
      return <ChatInput channelId={channelId} />;
    } else {
      return <ChatLoginPrompt />;
    }
  };

  if (!isLoaded || !channelId) {
    return <Loader fillComponent={true} hideOnNative={isMobileAppWebview} />;
  }

  if (isNativeAppWebview || isEmbed) {
    return (
      <>
        <SEOHeaderTags title={'Chat'} description={''} image={''} url={''} />
        <div className="tw-flex tw-flex-col tw-h-full tw-absolute tw-top-0 tw-left-0 tw-right-0 tw-bottom-0 tw-text-label [.creator-da_&]:tw-bg-white [.ios-creator_&]:tw-pb-safe [.ios-creator_&]:tw-pt-safe [.android-creator_&]:tw-pb-safe [.android-creator_&]:tw-pt-safe">
          <ChatMenu />
          <ChatScrollWrapper>
            {messages.length > 0 ? (
              <ChatComments messages={messages} />
            ) : (
              <ChatEmpty />
            )}
          </ChatScrollWrapper>
          {renderChatInput()}
        </div>
      </>
    );
  }

  return <Error404 />;
};
